import { Injectable } from '@angular/core';
import {
  applyPatches,
  enableMapSet,
  enablePatches,
  Patch,
  setAutoFreeze,
} from 'immer';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { LayoutStateService } from './layout-state.service';
import { LayoutMetricsService } from './layout-metrics.service';

enablePatches();
enableMapSet();
// Layout contains Maps; some downstream consumers still mutate the published
// layout in-place (e.g. dragging-flag setters) — keep autofreeze off so those
// paths don't blow up under immer.
setAutoFreeze(false);

interface UndoEntry {
  /** Patches that, applied to the post-mutation state, revert it. */
  inverse: Patch[];
  /** Patches that, applied to the pre-mutation state, redo it. */
  forward: Patch[];
}

/**
 * Patch-based undo/redo. Each mutation hands LayoutHistoryService the
 * inverse+forward patch pair captured by immer.produce; undo applies the
 * inverse to the current layout, redo replays the forward patches.
 *
 * Patches scale with what changed, not with layout size — replaces the
 * previous deep-clone-snapshot approach that grew O(layout) per mutation.
 *
 * Owns its own re-entrancy flag (`isUndoingRedoing`) so mutations triggered
 * during a replay don't recursively record new patches. Stacks are capped at
 * 50 entries each; oldest entries drop off the front when the cap is
 * exceeded.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutHistoryService {
  private undoStack: UndoEntry[] = [];
  private redoStack: UndoEntry[] = [];
  private maxStackSize = 50;
  private isUndoingRedoing = false;

  constructor(
    private state: LayoutStateService,
    private metrics: LayoutMetricsService
  ) {}

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** True while an undo or redo replay is in flight. Mutators consult this
   *  to skip recording patches against a replay. */
  isReplaying(): boolean {
    return this.isUndoingRedoing;
  }

  /**
   * Push the patch pair produced by the most recent mutation onto the undo
   * stack and clear redo. No-op while a replay is in flight, when called with
   * `skipUndo`, or when the patch pair is empty (no observable change).
   */
  recordPatches(
    inverse: Patch[],
    forward: Patch[],
    skipUndo: boolean = false
  ): void {
    if (skipUndo || this.isUndoingRedoing) return;
    if (inverse.length === 0 && forward.length === 0) return;

    this.redoStack = [];
    this.undoStack.push({ inverse, forward });
    if (this.undoStack.length > this.maxStackSize) {
      this.undoStack.shift();
    }
  }

  undo(): void {
    if (!this.canUndo()) return;

    this.isUndoingRedoing = true;
    try {
      const entry = this.undoStack.pop();
      const currentLayout = this.state.getLayoutValue();
      if (!entry || !currentLayout) return;

      const layout = applyPatches(currentLayout, entry.inverse);

      this.redoStack.push(entry);
      if (this.redoStack.length > this.maxStackSize) {
        this.redoStack.shift();
      }

      this.clearTransientFlags(layout);
      this.republish(layout);
    } finally {
      this.isUndoingRedoing = false;
    }
  }

  redo(): void {
    if (!this.canRedo()) return;

    this.isUndoingRedoing = true;
    try {
      const entry = this.redoStack.pop();
      const currentLayout = this.state.getLayoutValue();
      if (!entry || !currentLayout) return;

      const layout = applyPatches(currentLayout, entry.forward);

      this.undoStack.push(entry);
      if (this.undoStack.length > this.maxStackSize) {
        this.undoStack.shift();
      }

      this.republish(layout);
    } finally {
      this.isUndoingRedoing = false;
    }
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  private clearTransientFlags(layout: PlanogramLayout): void {
    layout.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.dragging = false;
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((product) => {
            product.dragging = false;
          });
        });
      });
    });
  }

  /** Mirror of the LayoutMutationService.updateLayout publish path, minus the
   *  patch-record step — undo/redo never record their own replays. */
  private republish(layout: PlanogramLayout): void {
    this.metrics.updateLayoutBreakdown(layout);
    this.metrics.updateLayoutDOSBreakdown(layout);
    this.metrics.updateLayoutCapacityBreakdown(layout);
    this.state.publishLayout(layout);
  }
}
